<template>
  <div class="flex gap-4 align-items-center w-fit" :style="{ height: '40px' }">
    <layout-settings :visible.sync="visibleSettingModal" />
    <layout-guide :visible.sync="visibleGuidesModal" />

    <alert-info v-if="hasAdminGroup" />
    <chat-info v-if="hasFeature('chat') && hasAdminGroup" />
    <layout-theme />

    <div class="action-button" @click="visibleGuidesModal = true">
      <a-icon width="24" height="24" icon="nimbus:question-circle" />
    </div>

    <a-dropdown>
      <div class="user-wrapper">
        <user-avatar :height="40" :width="40" :image-path="associate?.photo_url" />
        <div class="user-info">
          <p class="m-0" :style="{ fontSize: '16px' }">{{ associate?.first_name }} {{ associate?.last_name }}</p>
          <p class="m-0" :style="{ color: '#999999' }">
            {{ associate?.associate_number }}
            <template v-if="hasAdminGroup"
              ><a-divider type="vertical" /> {{ $store.state.applicationState.currentPaymentTypeName }}</template
            >
            <template><a-divider type="vertical" /> {{ associate?.role?.name }}</template>
          </p>
        </div>
        <div class="action-button">
          <a-icon width="16" height="16" icon="octicon:chevron-down-24" />
        </div>
      </div>
      <a-menu slot="overlay">
        <template v-if="hasAdminGroup">
          <a-sub-menu :disabled="loading">
            <div slot="title" class="flex gap-2 align-items-center">
              <a-icon width="20" height="20" icon="heroicons-outline:switch-vertical" />
              Change Profile
            </div>
            <a-menu-item
              v-for="associate in profiles"
              :key="`${associate.id}`"
              :disabled="loading"
              @click="onAssociateChange(currentProject.id, associate.associate_number)"
            >
              <div class="project-wrapper">
                <user-avatar :height="32" :width="32" :image-path="associate?.photo_url" />
                <div class="project-info">
                  <p :style="{ fontSize: '16px' }">{{ associate.first_name }} {{ associate.last_name }}</p>
                  <p :style="{ color: '#999999' }">
                    {{ associate.associate_number }}<a-divider type="vertical" />
                    {{ associate.role.name }}
                  </p>
                </div>
              </div>
            </a-menu-item></a-sub-menu
          ><a-sub-menu :disabled="loading">
            <div slot="title" class="flex gap-2 align-items-center">
              <a-icon width="20" height="20" icon="material-symbols:bookmark-outline" />
              Favorite Bookmarks
            </div>
            <template v-if="!!bookmarks.filter(item => item.is_favorite).length"
              ><a-menu-item
                :disabled="loading"
                v-for="bookmark in bookmarks.filter(item => item.is_favorite)"
                :key="bookmark.id"
                @click="handleOpenBookmark(bookmark.url)"
              >
                {{ bookmark.name }}
              </a-menu-item></template
            ><a-menu-item :disabled="loading" v-else> No Favorite Bookmarks Found </a-menu-item> </a-sub-menu
          ><a-menu-item :disabled="loading" v-if="hasPermission('manage_associates')">
            <div class="flex gap-2 align-items-center" @click="handleOpenProfile">
              <a-icon width="20" height="20" icon="mingcute:edit-line" />
              Edit Profile
            </div></a-menu-item
          ><a-menu-item :disabled="loading">
            <div class="flex gap-2 align-items-center" @click="visibleSettingModal = true">
              <a-icon width="20" height="20" icon="carbon:settings" />
              Settings
            </div></a-menu-item
          ></template
        ><a-menu-item :disabled="loading">
          <div
            class="flex gap-2 align-items-center"
            :style="{ color: 'rgb(255, 70, 70)' }"
            @click="handleSignOut"
            :disabled="loading"
          >
            <a-icon width="20" height="20" icon="radix-icons:exit" />
            Sign Out
          </div></a-menu-item
        ></a-menu
      ></a-dropdown
    >
  </div>
</template>

<script>
import { Divider, Dropdown, Menu, notification } from 'ant-design-vue'
import UserAvatar from '../ui/user-avatar.vue'
import { createNamespacedHelpers, mapActions, mapState } from 'vuex'
import AlertInfo from '@/components/layout/layout-alert-info.vue'
import permissionMixin from '@/mixins/permissionMixin'
import LayoutGuide from './layout-guide.vue'
import LayoutChatInfo from './layout-chat-info.vue'
import featureMixin from '@/mixins/featureMixin'
import LayoutThemeSwitcher from './layout-theme-switcher.vue'
import SettingsView from '@/modules/user/components/settings/settings-view.vue'

const { mapActions: modalActions } = createNamespacedHelpers('modals')
const { mapState: bookmarksState } = createNamespacedHelpers('bookmarks')

export default {
  components: {
    'a-divider': Divider,
    'a-dropdown': Dropdown,
    'a-menu': Menu,
    'a-sub-menu': Menu.SubMenu,
    'a-menu-item': Menu.Item,
    'chat-info': LayoutChatInfo,
    'user-avatar': UserAvatar,
    'alert-info': AlertInfo,
    'layout-guide': LayoutGuide,
    'layout-settings': SettingsView,
    'layout-theme': LayoutThemeSwitcher
  },
  mixins: [permissionMixin, featureMixin],
  data() {
    return {
      visibleGuidesModal: false,
      visibleSettingModal: false,

      theme: 'theme-light'
    }
  },
  computed: {
    ...bookmarksState({
      bookmarks: state => state.bookmarks
    }),
    ...mapState({
      loading: state => state.applicationState.loading
    }),
    currentProject() {
      return this.$store.state.applicationState.currentProject
    },
    associate() {
      return this.$store.state.applicationState.associate
    },
    profiles() {
      return (this.$store.state.applicationState?.associate?.profiles ?? []).filter(
        item => item.associate_number !== this.$store.state.applicationState?.associate?.associate_number
      )
    }
  },
  methods: {
    ...mapActions(['signOut', 'loadApplicationState', 'switchAssociate', 'setLoading']),
    ...modalActions(['openModal']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    handleOpenBookmark(url) {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.location.href = url
      } else {
        this.$router.push(url).catch(() => {})
      }
    },

    toggleTheme() {
      this.theme = this.theme === 'theme-light' ? 'theme-dark' : 'theme-light'
      document.body.className = this.theme
      localStorage.setItem('app-theme', this.theme)
    },

    handleOpenProfile() {
      this.openModal({
        type: 'profile',
        data: {
          visible: true,
          associate_number: this.$store.state.applicationState.associate.associate_number,
          edit: () => {},
          open_tab: 'personal'
        }
      })
    },

    async onAssociateChange(newProjectId, newAssociateNumber) {
      this.setLoading(true)

      try {
        await this.switchAssociate({
          project_id: newProjectId,
          current_associate_number: this.$store.state.applicationState.associate.associate_number,
          associate_number_to_switch: newAssociateNumber
        })
        await this.$router.replace({ query: {} }).catch(() => {})

        await this.loadApplicationState()

        this.showNotification('success', 'Success', `Associate successfully switched to ${newAssociateNumber}`)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        this.setLoading(false)
      }
    },

    async handleSignOut() {
      this.setLoading(true)
      let signedOut = false

      try {
        await this.signOut()
        signedOut = true

        this.$router.replace('/auth').catch(() => {})
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        if (!signedOut) {
          this.setLoading(false)
        }
      }
    }
  },
  mounted() {
    const saved = localStorage.getItem('app-theme')
    this.theme = saved || 'theme-light'
    document.body.className = this.theme
  }
}
</script>

<style scoped>
.user-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
}

.user-info {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.project-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

::v-deep .ant-divider {
  background: #999999 !important;
}
</style>
